<script setup lang="ts">
/**
 * QbAvatar — fourth QB atom. Displays initials derived from a name.
 *
 * Figma vars (avatar component):
 *   avatar/color        #4B5563  (initials text)
 *   avatar/background   #E5E7EB  (default light, used on light surfaces)
 *
 * Variants:
 *   • size: 'sm' (28), 'md' (36), 'lg' (48)
 *   • theme: 'light' (default) | 'dark' — dark variant for use on the navy
 *     Aside background, mirrors the brand surface.
 *
 * Initials algorithm:
 *   • 2+ words → first letter of each of the first two words
 *   • 1 word   → first two letters
 *   • Empty    → "?"
 *
 * Examples:
 *   "Administrator"   → "AD"
 *   "Daniel Mishtal"  → "DM"
 *   "john"            → "JO"
 *   "admin@qforb.net" → "AD" (treated as a single token)
 */

import { computed } from 'vue';

const props = withDefaults(
  defineProps<{
    name: string;
    size?: 'sm' | 'md' | 'lg';
    theme?: 'light' | 'dark';
  }>(),
  { size: 'md', theme: 'light' },
);

const initials = computed(() => {
  const raw = (props.name ?? '').trim();
  if (raw.length === 0) return '?';
  // Strip anything after '@' so email handles don't expose the domain.
  const handle = raw.split('@')[0]?.trim() ?? raw;
  const parts = handle.split(/[\s._-]+/).filter(Boolean);
  if (parts.length >= 2) {
    return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
  }
  return handle.slice(0, 2).toUpperCase();
});
</script>

<template>
  <span
    class="qb-avatar"
    :class="[`qb-avatar--${size}`, `qb-avatar--${theme}`]"
    :aria-label="name"
  >
    {{ initials }}
  </span>
</template>

<style scoped>
.qb-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  font-family: var(--qb-font-family);
  font-weight: var(--qb-font-weight-bold);
  letter-spacing: 0.02em;
  user-select: none;
  flex: 0 0 auto;
  line-height: 1;
}

/* ---- Size ---- */
.qb-avatar--sm { width: 28px; height: 28px; font-size: 11px; }
.qb-avatar--md { width: 36px; height: 36px; font-size: 13px; }
.qb-avatar--lg { width: 48px; height: 48px; font-size: 16px; }

/* ---- Theme ---- */
.qb-avatar--light {
  background: #E5E7EB;
  color: #4B5563;
}

.qb-avatar--dark {
  background: rgba(255, 255, 255, 0.92);
  color: #183462;
}
</style>
